import React, { FC, useCallback } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import classnames from 'classnames/bind';
import { Button, Pill } from '@clearcover/atlas';
import { Asset } from 'Shared/components/Asset';
import { FormInput } from 'Shared/components/FormInput';
import { useBatchBlockAttributes } from 'BlockList/hooks/useBatchBlockAttributes';
import { buildBatchPayloadFromPolicy } from 'BlockList/utils/buildBatchPayloadFromPolicy';
import { REASON_RADIO_OPTIONS } from 'BlockList/constants';
import { formatPhoneNumber } from 'Shared/utils';
import { BlockListModalShell } from 'BlockList/components/BlockListModalShell';
import type { GetPolicyResponseTypeV2 } from '@clearcover/core-app-types';
import type { ReasonKey } from 'BlockList/types';
import theme from 'BlockList/styles/theme.module.css';
import styles from './PolicyBlockModal.module.css';

const cx = classnames.bind(styles);

export type PolicyBlockModalProps = {
  open: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  policy: GetPolicyResponseTypeV2;
};

type FormValues = {
  reason: ReasonKey | '';
  notes: string;
};

const INITIAL_VALUES: FormValues = {
  reason: '',
  notes: '',
};

const validationSchema = Yup.object().shape({
  reason: Yup.string().required('Block reason is required'),
  notes: Yup.string().trim().required('Notes are required'),
});

type AssetVariant = React.ComponentProps<typeof Asset>['variant'];

type SectionHeaderProps = {
  icon?: AssetVariant;
  label: string;
  count?: number;
  hint?: string;
};

const SectionHeader: FC<SectionHeaderProps> = ({
  icon,
  label,
  count,
  hint,
}) => (
  <div className={styles.sectionHeader}>
    {icon && <Asset variant={icon} />}
    <span className={styles.sectionLabel}>{label}</span>
    {count !== undefined && (
      <span className={cx('pill', theme.pillCount)}>{count}</span>
    )}
    {hint && <span className={styles.sectionHint}>{hint}</span>}
  </div>
);

export const PolicyBlockModal: FC<PolicyBlockModalProps> = ({
  open,
  onDismiss,
  onSuccess,
  policy,
}) => {
  const { submitting, submit } = useBatchBlockAttributes();

  const handleSubmit = useCallback(
    async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
      if (!values.reason || !values.notes.trim()) {
        helpers.setSubmitting(false);
        return;
      }

      const payload = buildBatchPayloadFromPolicy(
        policy,
        values.reason as ReasonKey,
        values.notes.trim()
      );

      await submit(payload, {
        onSuccess: () => {
          helpers.resetForm();
          onSuccess();
        },
        onError: (message) => {
          helpers.setStatus(message);
          helpers.setSubmitting(false);
        },
      });
    },
    [policy, submit, onSuccess]
  );

  const {
    policyHolder,
    drivers = [],
    excludedDrivers = [],
    vehicles = [],
  } = policy;

  const email = policyHolder.contactInfo.email;
  const cellPhone = policyHolder.contactInfo.cellPhone ?? '';
  const homePhone = policyHolder.contactInfo.homePhone ?? '';
  const phoneRaw = cellPhone || homePhone;
  const phoneDisplay = phoneRaw
    ? formatPhoneNumber(phoneRaw) || phoneRaw
    : null;

  const allDrivers: Array<{
    driver: (typeof drivers)[0] | (typeof excludedDrivers)[0];
    isExcluded: boolean;
  }> = [
    ...drivers.map((d) => ({ driver: d, isExcluded: false })),
    ...excludedDrivers.map((d) => ({ driver: d, isExcluded: true })),
  ];

  return (
    <Formik
      key={open ? 'open' : 'closed'}
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        errors,
        touched,
        isValid,
        isSubmitting,
        status,
        submitForm,
      }) => {
        const inFlight = submitting || isSubmitting;

        // guardedDismiss is used by the Cancel button — covers Formik's
        // isSubmitting window. The closeGuard on the shell covers the
        // modal-level close X (outerGuardedDismiss equivalent).
        const guardedDismiss = () => {
          if (inFlight) return;
          onDismiss();
        };

        return (
          <BlockListModalShell
            open={open}
            onClose={onDismiss}
            // closeGuard gates the close X button: block when either the
            // network request (submitting) or Formik's isSubmitting is active.
            closeGuard={() => submitting || isSubmitting}
            title="Block policy attributes"
            icon="shieldOff"
            iconTone="danger"
            // 'tall' variant gives the modal more vertical breathing room so
            // the captured-attributes section + reason grid + notes don't
            // crowd against the footer at common viewports.
            modalVariant="tall"
            // Lock the modal to a 660px content-comfortable width. Pairing
            // minWidth and maxWidth pins Atlas's `.content` so the modal
            // stays at a stable design width independent of viewport (Atlas's
            // 'tall' variant max-width: 50vw would otherwise collapse it
            // below 660px on narrow viewports).
            minWidth={660}
            maxWidth={660}
            footer={
              <>
                {status && (
                  <div className={styles.footerError} role="alert">
                    {status}
                  </div>
                )}
                <Button
                  variant="secondary"
                  type="button"
                  onClick={guardedDismiss}
                  disabled={inFlight}
                  data-testid="policy-block-modal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  type="button"
                  isSubmitting={isSubmitting}
                  onClick={() => submitForm()}
                  disabled={!isValid || isSubmitting || submitting}
                  data-testid="policy-block-modal-submit"
                >
                  <span className={styles.blockButtonContent}>
                    <Asset variant="shieldOff" />
                    Block
                  </span>
                </Button>
              </>
            }
          >
            <Form>
              <SectionHeader label="CAPTURED ATTRIBUTES" />
              <div className={styles.capturedSection}>
                <SectionHeader
                  icon="account"
                  label="ACCOUNT"
                  hint="Attributes per policy"
                />
                <div className={styles.sectionContent}>
                  <div className={styles.accountGrid}>
                    <div className={styles.accountCell}>
                      <div className={styles.accountLabel}>Account Email</div>
                      <div
                        className={styles.accountValue}
                        data-testid="account-email"
                        title={email ?? ''}
                      >
                        {email ?? ''}
                      </div>
                    </div>
                    <div className={styles.accountCell}>
                      <div className={styles.accountLabel}>
                        Account Telephone
                      </div>
                      <div
                        className={styles.accountValue}
                        data-testid="account-phone"
                      >
                        {phoneDisplay ?? ''}
                      </div>
                    </div>
                  </div>
                </div>

                <SectionHeader
                  icon="driver"
                  label="DRIVERS"
                  count={drivers.length + excludedDrivers.length}
                  hint="Attributes per driver"
                />
                <div className={styles.sectionContent}>
                  {allDrivers.length === 0 ? (
                    <div
                      className={styles.emptyState}
                      data-testid="drivers-empty"
                    >
                      No drivers on file
                    </div>
                  ) : (
                    allDrivers.map(({ driver, isExcluded }, idx) => {
                      const isPrimary =
                        !isExcluded &&
                        (driver as (typeof drivers)[0])
                          .policyPartyRelationship === 'named_insured';

                      // excludedDrivers have no driversLicense field in the type
                      let dlDisplay = 'Not on file';
                      if (!isExcluded) {
                        const included = driver as (typeof drivers)[0];
                        const dlState = included.driversLicense?.state;
                        const dlNumber = included.driversLicense?.number;
                        if (dlState && dlNumber) {
                          dlDisplay = `${dlState} • ${dlNumber}`;
                        }
                      }

                      return (
                        <div
                          key={driver.id ?? idx}
                          className={styles.driverRow}
                          data-testid={`driver-row-${idx}`}
                        >
                          <div className={styles.driverHeaderRow}>
                            {driver.person.name.firstName}{' '}
                            {driver.person.name.lastName}
                            {isPrimary && (
                              <Pill
                                variant="primary"
                                className={styles.driverPill}
                                data-testid={`driver-primary-pill-${idx}`}
                              >
                                Primary
                              </Pill>
                            )}
                            {isExcluded && (
                              <Pill
                                variant="disabled"
                                className={styles.driverPill}
                                data-testid={`driver-excluded-pill-${idx}`}
                              >
                                Excluded
                              </Pill>
                            )}
                          </div>
                          <div className={styles.driverAttributesRow}>
                            <div>
                              <div className={styles.driverColHeader}>
                                DATE OF BIRTH
                              </div>
                              <div className={styles.driverColValue}>
                                {driver.person.dateOfBirth ?? ''}
                              </div>
                            </div>
                            <div>
                              <div className={styles.driverColHeader}>
                                DRIVER LICENSE (DL)
                              </div>
                              <div className={styles.driverColValue}>
                                {dlDisplay}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <SectionHeader
                  icon="car"
                  label="VEHICLES"
                  count={vehicles.length}
                  hint="Attributes per vehicle"
                />
                <div className={styles.sectionContent}>
                  {vehicles.length === 0 ? (
                    <div
                      className={styles.emptyState}
                      data-testid="vehicles-empty"
                    >
                      No vehicles on file
                    </div>
                  ) : (
                    vehicles.map((vehicle, idx) => (
                      <div
                        key={vehicle.vin ?? idx}
                        className={styles.vehicleRow}
                        data-testid={`vehicle-row-${idx}`}
                      >
                        <div className={styles.vehicleLabel}>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </div>
                        <div className={styles.vehicleVin}>{vehicle.vin}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div
                className={styles.reasonSection}
                role="radiogroup"
                aria-labelledby="block-reason-label"
              >
                <div className={styles.reasonLabelRow}>
                  <span id="block-reason-label" className={styles.reasonLabel}>
                    BLOCK REASON
                  </span>
                  <span className={styles.requiredMark}>*</span>
                </div>
                <div className={styles.reasonGrid}>
                  {REASON_RADIO_OPTIONS.map((opt) => (
                    <FormInput
                      key={opt.value}
                      variant="radio"
                      name="reason"
                      value={opt.value}
                      label={opt.label}
                      checked={values.reason === opt.value}
                      className={styles.reasonRadio}
                      onChecked={() => {
                        setFieldValue('reason', opt.value);
                        // shouldValidate=false avoids a stale revalidation
                        // race against setFieldValue's pending state commit.
                        setFieldTouched('reason', true, false);
                      }}
                    />
                  ))}
                </div>
                {touched.reason && !values.reason && errors.reason && (
                  <div role="alert" className={styles.reasonError}>
                    {errors.reason}
                  </div>
                )}
              </div>

              <div>
                <div className={styles.notesLabelRow}>
                  <span id="policy-notes-label" className={styles.notesLabel}>
                    {`Notes (${values.notes.length}/500)`}{' '}
                    <span className={styles.requiredMark}>*</span>
                  </span>
                  <span className={styles.notesHint}>required for audit</span>
                </div>
                <FormInput
                  variant="textarea"
                  name="notes"
                  label=""
                  aria-labelledby="policy-notes-label"
                  placeholder="Document the rationale. Visible in the audit log on the record's detail page."
                  value={values.notes}
                  maxLength={500}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFieldValue('notes', e.target.value)
                  }
                  onBlur={handleBlur}
                  errorMessage={touched.notes ? errors.notes : undefined}
                />
              </div>

              <div
                className={styles.requiredFootnote}
                data-testid="required-fields-footnote"
              >
                <span className={styles.requiredMark}>*</span> Required fields
              </div>
            </Form>
          </BlockListModalShell>
        );
      }}
    </Formik>
  );
};
